/**
 * Authentication hook
 *
 * Handles user authentication state and provides auth methods.
 */

import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../stores/userStore';
import { getMe, login as apiLogin, logout as apiLogout, updateProfile } from '../api/auth';
import { getPrimaryChart } from '../api/charts';

export function useAuth() {
  const { i18n } = useTranslation();
  const {
    user,
    isLoading,
    isAuthenticated,
    primaryChart,
    setUser,
    setLoading,
    setPrimaryChart,
    reset,
  } = useUserStore();

  /**
   * Check auth status on mount
   */
  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      setLoading(true);
      try {
        const userData = await getMe();
        if (mounted) {
          setUser(userData);
          // Sync i18n language with user preference
          if (userData?.language && userData.language !== i18n.language) {
            i18n.changeLanguage(userData.language);
          }
          // Load primary chart after auth
          if (userData) {
            try {
              const chart = await getPrimaryChart();
              if (mounted) {
                setPrimaryChart(chart);
              }
            } catch (e) {
              console.error('Failed to load primary chart:', e);
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (mounted) {
          setUser(null);
        }
      }
    }

    checkAuth();

    return () => {
      mounted = false;
    };
  }, [setUser, setLoading, setPrimaryChart, i18n]);

  /**
   * Login - redirects to Google OAuth
   */
  const login = useCallback(() => {
    apiLogin();
  }, []);

  /**
   * Logout - clears session and redirects
   */
  const logout = useCallback(() => {
    reset();
    apiLogout();
  }, [reset]);

  /**
   * Refresh user data
   */
  const refresh = useCallback(async () => {
    const userData = await getMe();
    setUser(userData);
    return userData;
  }, [setUser]);

  /**
   * Refresh primary chart data
   */
  const refreshPrimaryChart = useCallback(async () => {
    try {
      const chart = await getPrimaryChart();
      setPrimaryChart(chart);
      return chart;
    } catch (e) {
      console.error('Failed to refresh primary chart:', e);
      return null;
    }
  }, [setPrimaryChart]);

  /**
   * Manual auth check (for component mount)
   */
  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      const userData = await getMe();
      setUser(userData);
      return userData;
    } catch {
      setUser(null);
      return null;
    }
  }, [setUser, setLoading]);

  /**
   * Change language preference
   */
  const setLanguage = useCallback(async (language: string) => {
    // Update i18n immediately for responsive UI
    i18n.changeLanguage(language);

    // Update backend if authenticated
    if (user) {
      try {
        await updateProfile({ language });
        // Update user in store
        setUser({ ...user, language });
      } catch (error) {
        console.error('Failed to save language preference:', error);
        // Revert i18n if API fails
        if (user.language) {
          i18n.changeLanguage(user.language);
        }
      }
    }
  }, [user, setUser, i18n]);

  return {
    user,
    isLoading,
    isAuthenticated,
    primaryChart,
    language: user?.language || i18n.language,
    login,
    logout,
    refresh,
    refreshPrimaryChart,
    checkAuth,
    setLanguage,
  };
}

export default useAuth;
